/**
 * Générateur — Automatisme : reconnaître un triangle isocèle, équilatéral ou
 * rectangle à partir d'un schéma codé.
 * Attendu ID : 1076 | Niveau : 5ème | Domaine : Triangles
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Quelle est sa nature ?     (QCM un triangle + classer planche ①-④,
 *     carte FUSIONNÉE : même lecture de codage)
 *   - Quel codage te le dit ?    (QCM : la nature est DONNÉE, désigner le
 *     codage qui l'établit — la question 1 de la série flash)
 *   (format « Quels codages porter ? » retiré à la revue du 2026-09-02, décision prof)
 *
 * ── Plan de cartes validé (2026-09-02) ─────────────────────────────────────
 * Source : Automatisme n°38 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * APPUIS : les trois triangles sont nommés et définis dès le CM1 ; coder
 * angles droits et longueurs égales = automatisme de 6e. DOCTRINE (P10, carte
 * n°42 jumelle) : « seul le codage donne une information certaine », l'allure
 * ne prouve rien (flash Q6, question d.). CODAGES DU CYCLE 3 seulement :
 * traits d'égalité (1 ou 2) et petit carré d'angle droit — PAS d'arcs
 * d'angles égaux (ils appelleraient « deux angles égaux ⟹ isocèle », hors
 * automatisme). Pas de mesure, pas d'équerre à l'écran.
 * OBSTACLES P11 (fiche) : l'équilatéral EST isocèle (Q5 → consigne « nature
 * la plus précise ») ; « ça a l'air rectangle » sans petit carré (Q6) ; le
 * troisième côté « a l'air aussi long » (d.) ; un codage ajouté change la
 * nature, un codage absent n'interdit que de conclure (synthèse).
 * VIVIER FERMÉ (relu à la main) : rien / trait isolé / deux traits différents
 * → « on ne peut rien affirmer de plus » ; deux côtés au même trait → isocèle ; trois →
 * équilatéral ; un petit carré → rectangle ; petit carré + les deux côtés
 * ISSUS de ce sommet au même trait → rectangle isocèle. EXCLUS (impossibles) :
 * petit carré + trois côtés égaux, petit carré + côtés égaux dont
 * l'hypoténuse, deux petits carrés — garde par throw.
 * ALLURE : le dessin ne contredit JAMAIS le codage (throw) ; allure trompeuse
 * autorisée (un équilatéral dessiné, codé sur deux côtés → « isocèle ») ;
 * figure penchée au hasard.
 * INVARIANTS : conclusion du vivier === lecteur de codage (fonction pure,
 * double dérivation) ; nature géométrique du dessin au moins aussi précise
 * que la conclusion ; SVG avec data-figure (6 décimales) et data-code pour
 * les solveurs ; planche sans catégorie vide ; pas de calculatrice.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeClasser,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1076;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_NATURE = 'Quelle est sa nature ?';
const L_CODAGE = 'Quel codage te le dit ?';

// ─── Le lecteur de codage (règles cycle 3, fonction pure) ──────────────────
// Côtés : 0 = AB, 1 = BC, 2 = CA. Sommets : 0 = A, 1 = B, 2 = C.
// Les deux côtés issus du sommet v sont les côtés v et (v + 2) % 3.

const incident = (v) => [v, (v + 2) % 3];

const lireCodage = (marques, droits) => {
    const trois = marques.every((m) => m > 0 && m === marques[0]);
    const paires = [];
    for (let i = 0; i < 3; i++) for (let j = i + 1; j < 3; j++) if (marques[i] > 0 && marques[i] === marques[j]) paires.push([i, j]);
    const deux = !trois && paires.length === 1;
    const rect = droits.length === 1;
    if (rect && deux) return 'rectangle_isocele';
    if (rect) return 'rectangle';
    if (trois) return 'equilateral';
    if (deux) return 'isocele';
    return 'sansplus';
};

const NOMS_CONCLUSIONS = {
    sansplus: 'on ne peut rien affirmer de plus',
    isocele: 'un triangle isocèle',
    equilateral: 'un triangle équilatéral',
    rectangle: 'un triangle rectangle',
    rectangle_isocele: 'un triangle rectangle isocèle',
};

// Vivier FERMÉ : motifs de base (sommet particulier = A), décalés au tirage.
const MOTIFS_1076 = [
    { id: 'T1', marques: [0, 0, 0], droits: [], conclusion: 'sansplus' },
    { id: 'T2', marques: [1, 0, 0], droits: [], conclusion: 'sansplus' },
    { id: 'T3', marques: [1, 0, 2], droits: [], conclusion: 'sansplus' },
    { id: 'T4', marques: [1, 0, 1], droits: [], conclusion: 'isocele' },
    { id: 'T5', marques: [1, 1, 1], droits: [], conclusion: 'equilateral' },
    { id: 'T6', marques: [0, 0, 0], droits: [0], conclusion: 'rectangle' },
    { id: 'T7', marques: [1, 0, 0], droits: [0], conclusion: 'rectangle' },
    { id: 'T8', marques: [1, 0, 1], droits: [0], conclusion: 'rectangle_isocele' },
];

/** Garde du vivier : aucun motif impossible ou ambigu. */
MOTIFS_1076.forEach((m) => {
    if (m.droits.length > 1) throw new Error(`gen_1076 : motif ${m.id} — deux angles droits`);
    const trois = m.marques.every((x) => x > 0 && x === m.marques[0]);
    if (m.droits.length === 1 && trois) throw new Error(`gen_1076 : motif ${m.id} — équilatéral rectangle impossible`);
    if (m.droits.length === 1) {
        const v = m.droits[0];
        for (let i = 0; i < 3; i++) for (let j = i + 1; j < 3; j++) {
            if (m.marques[i] > 0 && m.marques[i] === m.marques[j]) {
                const inc = incident(v);
                if (!(inc.includes(i) && inc.includes(j))) throw new Error(`gen_1076 : motif ${m.id} — hypoténuse codée égale à un côté`);
            }
        }
    }
    if (lireCodage(m.marques, m.droits) !== m.conclusion) throw new Error(`gen_1076 : motif ${m.id} — conclusion contredite par le lecteur`);
});

/** Décale les indices (le sommet particulier change de lettre). */
const decalerMotif = (motif, s) => ({
    ...motif,
    marques: motif.marques.map((_, i) => motif.marques[(i - s + 3) % 3]),
    droits: motif.droits.map((d) => (d + s) % 3),
});

// ─── Géométrie du dessin : familles (sommet particulier = A), rotation ─────

const FAMILLES = {
    quelconque: () => { const cx = 2.6 + Math.random() * 0.8; const cy = 2.6 + Math.random() * 0.6; return [[0, 0], [5, 0], [cx, cy]]; },
    isocele: () => { const h = randomChoice([1.3, 5]); return [[0, h], [-2, 0], [2, 0]]; },
    equilateral: () => [[0, 0], [4, 0], [2, 2 * Math.sqrt(3)]],
    rectangle: () => { const b = randomChoice([2.4, 2.8]); return [[0, 0], [4, 0], [0, b]]; },
    rectangle_isocele: () => [[0, 0], [4, 0], [0, 4]],
};

const vec = (P, Q) => [Q[0] - P[0], Q[1] - P[1]];
const dot = (u, v) => u[0] * v[0] + u[1] * v[1];
const norme = (u) => Math.hypot(u[0], u[1]);
const cotes = (pts) => [vec(pts[0], pts[1]), vec(pts[1], pts[2]), vec(pts[2], pts[0])];

/** Nature géométrique du dessin (tolérance flottante), pour la garde. */
const natureDessin = (pts) => {
    const c = cotes(pts);
    const eq = (a, b) => Math.abs(norme(a) - norme(b)) < 1e-6 * Math.max(norme(a), norme(b));
    let egales = 0;
    for (let i = 0; i < 3; i++) for (let j = i + 1; j < 3; j++) if (eq(c[i], c[j])) egales++;
    const droitEn = (v) => { const [s1, s2] = incident(v); return Math.abs(dot(c[s1], c[s2])) < 1e-6 * norme(c[s1]) * norme(c[s2]); };
    const rect = [0, 1, 2].some(droitEn);
    if (egales === 3) return 'equilateral';
    if (rect && egales === 1) return 'rectangle_isocele';
    if (rect) return 'rectangle';
    if (egales === 1) return 'isocele';
    return 'quelconque';
};

const tourner = (pts, deg) => {
    const a = (deg * Math.PI) / 180; const co = Math.cos(a); const si = Math.sin(a);
    return pts.map(([x, y]) => [x * co - y * si, x * si + y * co]);
};

/** Le dessin respecte-t-il chaque codage ? */
const verifierCodageDessin = (pts, motif) => {
    const c = cotes(pts);
    for (let i = 0; i < 3; i++) for (let j = i + 1; j < 3; j++) {
        if (motif.marques[i] > 0 && motif.marques[i] === motif.marques[j] && Math.abs(norme(c[i]) - norme(c[j])) > 1e-6 * norme(c[i])) {
            throw new Error('gen_1076 : un codage d\'égalité contredit le dessin');
        }
    }
    motif.droits.forEach((v) => {
        const [s1, s2] = incident(v);
        if (Math.abs(dot(c[s1], c[s2])) > 1e-6 * norme(c[s1]) * norme(c[s2])) throw new Error('gen_1076 : un codage d\'angle droit contredit le dessin');
    });
};

const familleDessin = (conclusion, trompeuse) => {
    if (conclusion === 'equilateral') return 'equilateral';
    if (conclusion === 'rectangle_isocele') return 'rectangle_isocele';
    if (conclusion === 'rectangle') return trompeuse ? 'rectangle_isocele' : 'rectangle';
    if (conclusion === 'isocele') return trompeuse ? randomChoice(['equilateral', 'rectangle_isocele']) : 'isocele';
    return trompeuse ? randomChoice(['isocele', 'equilateral', 'rectangle', 'rectangle_isocele']) : 'quelconque';
};

const NATURE_DESSIN_ADMISE = {
    sansplus: ['quelconque', 'isocele', 'equilateral', 'rectangle', 'rectangle_isocele'],
    isocele: ['isocele', 'equilateral', 'rectangle_isocele'],
    equilateral: ['equilateral'],
    rectangle: ['rectangle', 'rectangle_isocele'],
    rectangle_isocele: ['rectangle_isocele'],
};

/** Figure codée complète : points, motif (décalé), allure — gardes passées. */
const construireFigure = (motifId, forcerFamille = null) => {
    const base = motifParId(motifId);
    const s = randInt(0, 2);
    const motif = decalerMotif(base, s);
    const trompeuse = Math.random() < 0.35;
    const famille = forcerFamille || familleDessin(motif.conclusion, trompeuse);
    const rotation = Math.random() < 0.3 ? 0 : randInt(5, 355);
    // Le sommet particulier du dessin est A avant décalage : on décale les
    // points comme le motif (le sommet particulier suit la lettre).
    const local = FAMILLES[famille]();
    const decale = [0, 1, 2].map((i) => local[(i - s + 3) % 3]);
    const pts = tourner(decale, rotation);
    verifierCodageDessin(pts, motif);
    const nature = natureDessin(pts);
    if (!NATURE_DESSIN_ADMISE[motif.conclusion].includes(nature)) {
        throw new Error(`gen_1076 : dessin ${nature} incompatible avec la conclusion ${motif.conclusion}`);
    }
    return { pts, motif, famille, rotation, trompeuse, natureDessin: nature, decalage: s };
};

const motifParId = (id) => MOTIFS_1076.find((m) => m.id === id);

// ─── Rendu SVG (triangle, traits, petits carrés, étiquettes, données) ──────

const NOMS_SOMMETS = ['A', 'B', 'C'];

const dessinerFigure = (fig, cadre) => {
    const { pts, motif } = fig;
    const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
    const xmin = Math.min(...xs); const xmax = Math.max(...xs);
    const ymin = Math.min(...ys); const ymax = Math.max(...ys);
    const marge = 30;
    const echelle = Math.min((cadre.w - 2 * marge) / (xmax - xmin), (cadre.h - 2 * marge) / (ymax - ymin));
    const X = (x) => cadre.x + marge + (x - xmin) * echelle + ((cadre.w - 2 * marge) - (xmax - xmin) * echelle) / 2;
    const Y = (y) => cadre.y + marge + (ymax - y) * echelle + ((cadre.h - 2 * marge) - (ymax - ymin) * echelle) / 2;
    const P = pts.map((p) => [X(p[0]), Y(p[1])]);
    const cx = (P[0][0] + P[1][0] + P[2][0]) / 3; const cy = (P[0][1] + P[1][1] + P[2][1]) / 3;

    let s = `<path d="M ${P[0][0].toFixed(1)} ${P[0][1].toFixed(1)} L ${P[1][0].toFixed(1)} ${P[1][1].toFixed(1)} L ${P[2][0].toFixed(1)} ${P[2][1].toFixed(1)} Z" fill="#eff6ff" stroke="#1E3A8A" stroke-width="2.2"/>`;
    for (let i = 0; i < 3; i++) {
        const n = motif.marques[i];
        if (!n) continue;
        const A = P[i]; const B = P[(i + 1) % 3];
        const mx = (A[0] + B[0]) / 2; const my = (A[1] + B[1]) / 2;
        const d = [B[0] - A[0], B[1] - A[1]]; const L = norme(d);
        const ux = d[0] / L; const uy = d[1] / L; const nx = -uy; const ny = ux;
        for (let t = 0; t < n; t++) {
            const off = (t - (n - 1) / 2) * 5;
            const px = mx + ux * off; const py = my + uy * off;
            s += `<line x1="${(px - nx * 6).toFixed(1)}" y1="${(py - ny * 6).toFixed(1)}" x2="${(px + nx * 6).toFixed(1)}" y2="${(py + ny * 6).toFixed(1)}" stroke="#111827" stroke-width="2" data-code="tick" data-cote="${i}" data-n="${n}"/>`;
        }
    }
    motif.droits.forEach((v) => {
        const S = P[v]; const prev = P[(v + 2) % 3]; const next = P[(v + 1) % 3];
        const a = vec(S, prev); const b = vec(S, next);
        const ua = [a[0] / norme(a), a[1] / norme(a)]; const ub = [b[0] / norme(b), b[1] / norme(b)];
        const k = 11;
        const p1 = [S[0] + ua[0] * k, S[1] + ua[1] * k];
        const p2 = [S[0] + (ua[0] + ub[0]) * k, S[1] + (ua[1] + ub[1]) * k];
        const p3 = [S[0] + ub[0] * k, S[1] + ub[1] * k];
        s += `<polyline points="${p1[0].toFixed(1)},${p1[1].toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)} ${p3[0].toFixed(1)},${p3[1].toFixed(1)}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre" data-sommet="${v}"/>`;
    });
    P.forEach((p, i) => {
        const d = [p[0] - cx, p[1] - cy]; const L = norme(d) || 1;
        const lx = p[0] + (d[0] / L) * 15; const ly = p[1] + (d[1] / L) * 15 + 5;
        s += `<circle cx="${p[0].toFixed(1)}" cy="${p[1].toFixed(1)}" r="2.4" fill="#1e293b"/>`
            + `<text x="${lx.toFixed(1)}" y="${ly.toFixed(1)}" font-size="13" font-weight="700" fill="#1e293b" text-anchor="middle">${NOMS_SOMMETS[i]}</text>`;
    });
    return s;
};

const dataFigure = (fig) => fig.pts.map((p, i) => `${NOMS_SOMMETS[i]}:${p[0].toFixed(6)},${p[1].toFixed(6)}`).join('|');

const svgFigure = (fig) => {
    const w = 320; const h = 240;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial" data-figure="${dataFigure(fig)}">${dessinerFigure(fig, { x: 0, y: 0, w, h })}</svg>`;
};

const svgPlanche = (figs) => {
    const w = 190; const h = 200; const numeros = ['①', '②', '③', '④'];
    let s = '';
    figs.forEach((fig, i) => {
        const x = i * w;
        s += `<g data-index="${i + 1}"><text x="${x + w / 2}" y="22" font-size="18" font-weight="800" fill="#1E3A8A" text-anchor="middle">${numeros[i]}</text>`
            + dessinerFigure(fig, { x, y: 26, w, h: h - 26 }) + '</g>';
    });
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w * 4} ${h}" width="${w * 4}" height="${h}" font-family="Arial">${s}</svg>`;
};

const motifsDe = (conclusion) => MOTIFS_1076.filter((m) => m.conclusion === conclusion);

// ─── Format 1a : QCM nature ────────────────────────────────────────────────

let derniereSignatureNature1076 = null;

const genNatureQcm = () => {
    let payload = construireNatureQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureNature1076; t++) payload = construireNatureQcm();
    derniereSignatureNature1076 = payload._meta.params.signature;
    return payload;
};

const construireNatureQcm = () => {
    // Conclusion tirée d'abord (5 natures équiprobables), puis un motif.
    const conclusion = randomChoice(Object.keys(NOMS_CONCLUSIONS));
    const fig = construireFigure(randomChoice(motifsDe(conclusion)).id);
    const correct = NOMS_CONCLUSIONS[fig.motif.conclusion];
    const autres = shuffle(Object.keys(NOMS_CONCLUSIONS).filter((c) => c !== fig.motif.conclusion)).slice(0, 3).map((c) => NOMS_CONCLUSIONS[c]);
    const texte = '<p>ABC est un triangle.</p><p>D\'après son codage, quelle est sa nature la plus précise ?</p>';
    const payload = makeQcm([correct, ...autres], correct, texte, {
        ...opts(L_NATURE), elements_additionnels: imgElement(svgFigure(fig), 'medium'),
    });
    payload._meta.params = { motif: fig.motif.id, conclusion, famille: fig.famille, trompeuse: fig.trompeuse, rotation: fig.rotation, signature: `${fig.motif.id}-${fig.famille}-${fig.decalage}` };
    return payload;
};

// ─── Format 1b : classer une planche de 4 triangles ────────────────────────

let derniereSignatureClasser1076 = null;

const genNatureClasser = () => {
    let payload = construireNatureClasser();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureClasser1076; t++) payload = construireNatureClasser();
    derniereSignatureClasser1076 = payload._meta.params.signature;
    return payload;
};

const construireNatureClasser = () => {
    // 4 natures parmi 5, une figure par nature : aucune catégorie vide.
    const conclusions = shuffle(Object.keys(NOMS_CONCLUSIONS)).slice(0, 4);
    const figs = conclusions.map((c) => construireFigure(randomChoice(motifsDe(c)).id));
    // Les figures sont présentées dans un ordre mélangé (pas celui des catégories).
    const ordre = shuffle([0, 1, 2, 3]);
    const figsOrdre = ordre.map((k) => figs[k]);
    const categoriesOrdre = conclusions.map((c, k) => ({
        name: NOMS_CONCLUSIONS[c].charAt(0).toUpperCase() + NOMS_CONCLUSIONS[c].slice(1),
        items: [`Figure ${ordre.indexOf(k) + 1}`],
    }));
    const payload = makeClasser(
        categoriesOrdre,
        '<p>Les quatre figures sont des triangles. Classe chacune d\'après son codage.</p>',
        { ...opts(L_NATURE), elements_additionnels: imgElement(svgPlanche(figsOrdre), 'large') },
    );
    payload._meta.params = { conclusions: ordre.map((k) => conclusions[k]), signature: ordre.map((k) => figs[k].motif.id).join('-') };
    return payload;
};

// ─── Format 2 : quel codage te le dit ? ────────────────────────────────────

const lettresSommet = (v) => NOMS_SOMMETS[v];
const lettresCote = (i) => `[${NOMS_SOMMETS[i]}${NOMS_SOMMETS[(i + 1) % 3]}]`;

/** Le codage qui établit la conclusion, décrit avec les lettres réelles. */
const codageQuiEtablit = (motif) => {
    const { marques, droits, conclusion } = motif;
    const paire = [];
    for (let i = 0; i < 3; i++) for (let j = i + 1; j < 3; j++) if (marques[i] > 0 && marques[i] === marques[j]) paire.push(i, j);
    // Gabarits alignés sur ceux des pièges (revue 2026-09-02, A2).
    if (conclusion === 'equilateral') return 'Le même trait porté sur chacun des trois côtés.';
    if (conclusion === 'isocele') return `Le même trait porté sur ${lettresCote(paire[0])} et sur ${lettresCote(paire[1])}.`;
    if (conclusion === 'rectangle') return `Le petit carré d'angle droit porté au sommet ${lettresSommet(droits[0])}.`;
    // rectangle isocèle
    return `Le petit carré en ${lettresSommet(droits[0])} et le même trait sur ${lettresCote(paire[0])} et ${lettresCote(paire[1])}.`;
};

/** Une paire de côtés qui n'est PAS codée égale (pour le piège « mauvaise paire »). */
const paireNonCodee = (marques) => {
    const paires = [[0, 1], [1, 2], [0, 2]].filter(([i, j]) => !(marques[i] > 0 && marques[i] === marques[j]));
    return paires.length ? randomChoice(paires) : null;
};

let derniereSignatureCodage1076 = null;

const genCodageQcm = () => {
    let payload = construireCodageQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureCodage1076; t++) payload = construireCodageQcm();
    derniereSignatureCodage1076 = payload._meta.params.signature;
    return payload;
};

const construireCodageQcm = () => {
    const conclusion = randomChoice(['isocele', 'equilateral', 'rectangle', 'rectangle_isocele']);
    const fig = construireFigure(randomChoice(motifsDe(conclusion)).id);
    const { marques, droits } = fig.motif;
    const bonne = codageQuiEtablit(fig.motif);
    const nom = NOMS_CONCLUSIONS[conclusion];
    // Pièges : jamais recevables par construction — l'allure, la règle, un
    // codage ABSENT de la figure (faux), ou un codage présent mais INSUFFISANT.
    // Gabarits homogènes (35-50 caractères) et vivier élargi (revue 2026-09-02).
    const candidats = [
        `Il a l'air ${nom.replace('un triangle ', '')} quand on regarde le dessin.`,
        'À la règle, ses côtés font le même nombre de cm.',
        'Deux traits portés sur chacun des trois côtés.',                                                                   // jamais porté
    ];
    const sansCarre = droits.length === 0;
    if (sansCarre) candidats.push(`Le petit carré d'angle droit porté au sommet ${lettresSommet(randInt(0, 2))}.`);           // absent
    if (conclusion !== 'equilateral') candidats.push('Le même trait porté sur chacun des trois côtés.');                      // absent
    if (conclusion === 'rectangle_isocele') {
        candidats.push(`Le petit carré d'angle droit au sommet ${lettresSommet(droits[0])}, à lui seul.`);                   // insuffisant
    }
    if (conclusion === 'rectangle' || conclusion === 'equilateral') {
        const i = marques.findIndex((m) => m > 0);
        candidats.push(`Le trait porté sur ${lettresCote(i >= 0 ? i : 0)} seulement, sans autre codage.`);                   // ne dit rien
    }
    const mauvaise = paireNonCodee(marques);
    if (mauvaise) candidats.push(`Le même trait porté sur ${lettresCote(mauvaise[0])} et sur ${lettresCote(mauvaise[1])}.`);  // paire non codée
    const pieges = shuffle(candidats.filter((c) => c !== bonne)).slice(0, 3);
    if (pieges.length < 3) throw new Error('gen_1076 : pièges de codage insuffisants');

    const texte = `<p>D'après son codage, ABC est ${nom}.</p><p>Quel codage te le dit ?</p>`;
    const payload = makeQcm([bonne, ...pieges], bonne, texte, {
        ...opts(L_CODAGE), elements_additionnels: imgElement(svgFigure(fig), 'medium'),
    });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    payload._meta.params = { motif: fig.motif.id, conclusion, signature: `${fig.motif.id}-${fig.decalage}-${pieges.map((p) => p.slice(0, 8)).sort().join('')}` };
    return payload;
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    nature_qcm_unique: genNatureQcm,
    nature_classer: genNatureClasser,
    codage_qcm_unique: genCodageQcm,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', classer: 'Classer' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'codage', label: L_CODAGE, formatsList: buildFormats('codage') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
